import { Injectable, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { BaseService } from '../core/services/base.service';
import { API_VERSION_ENUM, MICROSERVICE_ENUM } from '../consts/api.const';

/**
 * MCP 认证服务。
 *
 * 使用 TVMS 快捷查询（通用 Dify 助手）之前，需先调用 statusapi 的 signin 接口，
 * 换取一个 MCP 专用 token，再把这个 token 作为 `X_MCP_Signature` 传给 Dify chatflow，
 * Dify 侧据此校验用户身份/权限。
 *
 * 地址复用 `BaseService` 的微服务拼接（host + `/statusapi/v3.0`），不重复配置环境变量。
 * 认证说明：signin 接口需要当前登录用户的认证（`www-authenticate: Bearer`）。
 * 通过 HttpClient（BaseService）调用会自动经过 `authInterceptor`，附带 TVMS 的
 * MSAL access token（`Authorization: Bearer <token>`）完成身份认证。
 */
@Injectable({ providedIn: 'root' })
export class MCPAuthService {
  private readonly baseService = inject(BaseService);

  /** 获取 MCP token（成功返回 token 字符串，失败返回空串） */
  async signin(): Promise<string> {
    try {
      const raw = await lastValueFrom<string>(
        this.baseService.postV3<string>(
          'mcp/auth/signin',
          '',
          undefined,
          undefined,
          undefined,
          'text' as any,
          MICROSERVICE_ENUM.status,
          API_VERSION_ENUM.v3
        )
      );
      return this.extractToken(raw);
    } catch {
      // signin 失败不阻塞聊天主流程（X_MCP_Signature 留空，仍可尝试对话）
      return '';
    }
  }

  /**
   * 从 signin 响应中提取 token。
   * 响应为 `text/plain`，可能是裸 token 字符串，也可能是 JSON 结构，
   * 兼容多种常见字段名（token / accessToken / access_token / data）。
   */
  private extractToken(raw: string): string {
    if (!raw) return '';
    const text = raw.trim();
    // 去掉可能的外层引号
    const cleaned = text.replace(/^["']|["']$/g, '');
    // 尝试按 JSON 解析，取常见 token 字段
    if (cleaned.startsWith('{')) {
      try {
        const obj = JSON.parse(cleaned) as Record<string, unknown>;
        const value =
          obj['token'] ??
          obj['accessToken'] ??
          obj['access_token'] ??
          obj['data'] ??
          obj['result'];
        if (typeof value === 'string') return value.trim();
        if (value && typeof value === 'object') {
          const nested = value as Record<string, unknown>;
          const inner = nested['token'] ?? nested['accessToken'] ?? nested['access_token'];
          if (typeof inner === 'string') return inner.trim();
        }
        return '';
      } catch {
        /* 非 JSON，走裸字符串 */
      }
    }
    return cleaned;
  }
}
